/**
 * Round 16 D18 -- whose a stated duration is.
 *
 * 24h set by the bank on SAMA 1's head office read "24h stated" on the
 * scenario and "scenario states 24h" in the war room, under a picker
 * promising every figure is SAMA's. One sentence for both surfaces:
 * a figure somebody set says who and when and what the source states;
 * an untouched figure says it is the source's own.
 */
import type { TFunction } from "i18next";

import { formatDate } from "shared/time/formatDate";

export interface DurationProvenance {
  set_by?: string | null | undefined;
  set_at?: string | null | undefined;
  source_label?: string | null | undefined;
  source_basis?: string | null | undefined;
  source_hours?: number | null | undefined;
  source_hours_max?: number | null | undefined;
  /** Round 17 D23 -- the figure in force, to tell a setting that
   *  restores the source's own figure from one that departs from it. */
  basis?: string | null | undefined;
  hours?: number | null | undefined;
  hours_max?: number | null | undefined;
}

/** Round 17 D23 -- the figure in force is the source's own. */
export function matchesSource(p: DurationProvenance): boolean {
  if (p.basis == null || p.source_basis == null) return false;
  if (p.basis !== p.source_basis) return false;
  if (p.basis !== "stated") return true;
  return (
    (p.hours ?? null) === (p.source_hours ?? null) &&
    (p.hours_max ?? null) === (p.source_hours_max ?? null)
  );
}

/** Round 17 D23 -- a figure somebody set that is not the source's. */
export function isBankFigure(p: DurationProvenance): boolean {
  return Boolean(p.set_by) && !matchesSource(p);
}

/** What the source states, as a phrase, for sentences outside here. */
export function sourceFigurePhrase(t: TFunction, p: DurationProvenance): string {
  return sourceFigure(t, p);
}

/** What the source states, as a phrase: "none", "72h", "5 to 12h". */
function sourceFigure(t: TFunction, p: DurationProvenance): string {
  if (p.source_basis === "stated" && p.source_hours != null) {
    return p.source_hours_max != null && p.source_hours_max > p.source_hours
      ? t("scenarioProvenance.range", { from: p.source_hours, to: p.source_hours_max })
      : t("scenarioProvenance.hours", { n: p.source_hours });
  }
  if (p.source_basis === "until_recovered")
    return t("scenarioProvenance.untilRecovered");
  return t("scenarioProvenance.none");
}

/** The sentence, or null where there is nothing to say beyond the figure
 *  (no template and nobody has changed it). */
export function durationProvenanceSentence(
  t: TFunction,
  lang: string,
  p: DurationProvenance,
): string | null {
  // Round 17 D23 -- putting the source's own figure back clears the mark:
  // "Set by Noura; SAMA states 72h" over 72h called SAMA's figure hers.
  if (p.set_by && !matchesSource(p)) {
    const when = formatDate(p.set_at ?? null, lang);
    return p.source_label
      ? t("scenarioProvenance.setBySource", {
          who: p.set_by,
          when,
          source: p.source_label,
          figure: sourceFigure(t, p),
        })
      : t("scenarioProvenance.setBy", { who: p.set_by, when });
  }
  if (p.source_label) {
    return t("scenarioProvenance.sourceStates", {
      source: p.source_label,
      figure: sourceFigure(t, p),
    });
  }
  return null;
}
